/* eslint-disable react/jsx-no-duplicate-props */
// eslint-disable-next-line no-restricted-imports
import { ChangeEvent, KeyboardEvent, Ref, forwardRef, useMemo } from 'react';

import { useTranslation } from 'libs/i18n';

import { Box } from '../Box';
import { CopyButton } from '../CopyButton';
import { Stack } from '../Stack';
import { Typography } from '../Typography';
import { Colors } from '../theme';

import { StyledInput } from './Input.styles';
import { InputLabel } from './templates';
import { InputProps } from './types';

/**
 * Input component.
 */
export const Input = forwardRef(
  (
    {
      onChange,
      onChangeText,
      onSubmit,
      onKeyDown,
      rightStartElement,
      rightEndElement,
      leftElement,
      inputRef,
      error,
      fullWidth,
      maxLength,
      trim,
      readOnly,
      mode = 'standard',
      showCopyButton,
      formatValue = (v) => v,
      parseValue = (v) => v,
      label,
      showRequiredMark,
      ...props
    }: InputProps,
    ref: Ref<HTMLDivElement>
  ) => {
    const { t } = useTranslation();

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      let { value } = event.target;

      value = value ? String(parseValue(value)) : value;
      if (maxLength !== undefined && event.target.value.length > maxLength) {
        value = event.target.value.slice(0, maxLength);
      }
      event.target.value = value;

      onChange?.(event);
      if (trim === 'all') {
        value = value?.trim();
      } else if (trim === 'end') {
        value = value?.trimEnd();
      } else if (trim === 'start') {
        value = value?.trimStart();
      }

      onChangeText?.(value, event);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        onSubmit?.(e);
      }
      onKeyDown?.(e);
    };

    const title =
      props.value &&
      (typeof props.value === 'string' || typeof props.value === 'number')
        ? props.value.toString()
        : undefined;

    const formattedValue = formatValue(props.value);

    const inputLabel = useMemo(() => {
      if (!label || typeof label !== 'string') {
        return label;
      }

      return (
        <Stack alignItems="center" direction="row" spacing={4}>
          <InputLabel label={label} />
          {!readOnly && showRequiredMark && (
            <Typography
              color={Colors.blue700}
              title={t('components.input.requiredTitle')}
              variant="caption"
            >
              *
            </Typography>
          )}
        </Stack>
      );
    }, [label, readOnly, showRequiredMark, t]);

    return (
      <StyledInput
        ref={ref}
        error={Boolean(error)}
        fullWidth={fullWidth}
        inputRef={inputRef}
        label={inputLabel}
        mode={mode}
        variant="standard"
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        {...props}
        slotProps={{
          input: {
            readOnly,
            title,
            className: 'input-root',
            ...props.InputProps,
            endAdornment: (
              <Stack alignItems="center" direction="row" spacing={8}>
                {showCopyButton && (
                  <Box className="input-copy">
                    <CopyButton interactive value={formattedValue} />
                  </Box>
                )}
                {rightStartElement}
                {props.InputProps?.endAdornment}
                {rightEndElement}
              </Stack>
            ),
            startAdornment: (
              <>
                {leftElement}
                {props.InputProps?.startAdornment}
              </>
            ),
          },
        }}
        value={formattedValue}
      />
    );
  }
);
